class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  }

  onMouseEnter() {
    if (!this.hoverQuery.matches) return;
    clearTimeout(this.leaveTimeout);
    clearTimeout(this.enterTimeout);

    // Re-entered mid-collapse: play the close backwards rather than cancelling
    // it. A bare cancel() drops the clip-path and the fill:forwards opacity
    // fade in a single frame, so the panel snaps back to full size. [open] is
    // deliberately left alone — the panel never actually shut, so there is
    // nothing to reopen and no hover-intent delay to wait out.
    if (this.closing && this.closeAnimation) {
      this.closing = false;
      this.closeAnimation.onfinish = null;
      this.closeAnimation.oncancel = null;
      this.content.style.zIndex = '';
      this.closeAnimation.reverse();
      this.fadeAnimation?.reverse();
      this.closeAnimation.onfinish = () => {
        // Back to full size — hand the resting state back to CSS.
        this.closeAnimation?.cancel();
        this.fadeAnimation?.cancel();
        this.closeAnimation = null;
        this.fadeAnimation = null;
      };
      return;
    }

    // Require a 200ms hover intent before opening the mega menu.
    this.enterTimeout = setTimeout(() => this.open(), 200);
  }

  open() {
    if (!this.hoverQuery.matches) return;
    document.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
      if (openDetails !== this.mainDetailsToggle) openDetails.closest('header-menu').close();
    });
    if (!this.mainDetailsToggle.open) {
      this.mainDetailsToggle.open = true;
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    }
  }

  // Open reveal, mirroring close(). Deliberately NOT a CSS animation keyed on
  // .mega-menu[open]: Chrome hides closed <details> content with
  // content-visibility: hidden (::details-content) and defers style recalc for
  // the skipped subtree, so removing [open] often never cancels the CSS
  // animation. The next open then re-matches the same animation-name and the
  // engine continues the already-finished animation instead of restarting it,
  // snapping the panel open. element.animate() creates a fresh animation every
  // time. See the note in section-header-custom.css.
  reveal() {
    this.closing = false;
    this.closeAnimation?.cancel();
    this.fadeAnimation?.cancel();
    this.closeAnimation = null;
    this.fadeAnimation = null;

    this.openAnimation?.cancel();
    this.contentFadeAnimation?.cancel();
    this.openAnimation = null;
    this.contentFadeAnimation = null;
    this.content.style.zIndex = '';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.openAnimation = this.content.animate(
      [{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0 0)' }],
      { duration: 300, easing: 'ease' }
    );
    this.contentFadeAnimation = this.content.firstElementChild?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 350,
      delay: 150,
      easing: 'cubic-bezier(0, 0, 0.3, 1)',
      fill: 'backwards',
    });
  }

  onMouseLeave() {
    if (!this.hoverQuery.matches) return;
    clearTimeout(this.enterTimeout);
    this.leaveTimeout = setTimeout(() => {
      // A mouseleave can be spurious: the panel shrinking under the cursor on
      // a sub-link switch, or the clip-path reveal not being hit-testable yet,
      // both fire it while the pointer never visually left. :hover reflects
      // the live hit-test, so re-check before actually closing.
      if (this.matches(':hover')) return;
      this.close();
    }, 200);
  }

  close() {
    if (!this.mainDetailsToggle.open || this.closing) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const options = { duration: reduced ? 0 : 300, easing: 'ease' };
    this.closing = true;
    this.openAnimation?.cancel();
    this.contentFadeAnimation?.cancel();
    this.openAnimation = null;
    this.contentFadeAnimation = null;

    // Switching menus overlaps this collapse with the next panel's reveal.
    // Panels are siblings in one stacking context, all at z-index -1 from
    // .global-settings-popup, so they otherwise sort by DOM order and the
    // outgoing panel covers the incoming one whenever the pointer moves
    // leftwards along the nav. Drop the one that is leaving.
    this.content.style.zIndex = '-2';

    this.fadeAnimation = this.content.firstElementChild?.animate([{ opacity: 1 }, { opacity: 0 }], {
      ...options,
      fill: 'forwards',
    });
    this.closeAnimation = this.content.animate(
      [{ clipPath: 'inset(0 0 0 0)' }, { clipPath: 'inset(0 0 100% 0)' }],
      options
    );
    this.closeAnimation.onfinish = () => {
      this.closing = false;
      super.close();
      this.fadeAnimation?.cancel();
      this.content.style.zIndex = '';
      this.closeAnimation = null;
      this.fadeAnimation = null;
    };
    this.closeAnimation.oncancel = () => {
      this.closing = false;
      this.fadeAnimation?.cancel();
      this.content.style.zIndex = '';
      this.closeAnimation = null;
      this.fadeAnimation = null;
    };
  }

  onToggle() {
    // Mirrors details[open] as an attribute on <header-menu> itself so CSS
    // (the hover-bridge ::after in section-header-custom.css) doesn't need
    // :has() — Safari misses/mishandles :has([open]) invalidation, which
    // left the bridge missing and the menu closing when the pointer crossed
    // the strip between the nav item and the panel.
    this.toggleAttribute('data-menu-open', this.mainDetailsToggle.open);

    // Covers every way the panel opens — hover intent, summary click, tap and
    // keyboard — since they all land on the details `toggle` event.
    if (this.mainDetailsToggle.open) this.reveal();

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom / viewportZoom())}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
